import { Chip, Grid, Skeleton, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { InterventionDto, InterventionState, SearchInterventionsDto } from '../../../services/dtos/assignments.dto';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import BuildIcon from '@mui/icons-material/Build';
import NoData from '../../commun/no-data';
import StyledTable from '../../commun/table';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import { serviceSearchInterventions } from '../../../services/assignments.service';
import InterventionsForm from '../../forms/assignment/interventions.form';

/**
 * Ui de l'onglet decision & decomposition
 * @constructor
 */
export default function InterventionsUi(
    props: Readonly<{
        mandate: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        confirmationProposalAssignment: boolean;
    }>
) {
    //SnackBar pour les messages
    const showMessage = useSnackbars();

    //Critères de recherche onglet decisions et deliberations
    const [searchInterventions, setSearchInterventions] = useState<SearchInterventionsDto>({
        page: 0,
        itemsPerPage: 10,
        mandateId: props.mandate.id,
    });
    //Searching
    const [searching, setSearching] = useState<boolean>(false);
    // result tableau decisions
    const [interventions, setInterventions] = useState<ResultSearchDto<InterventionDto>>();

    //Lancement de la recherche a chaque changement
    useEffect(() => {
        let isMounted = true;

        setSearching(true);
        const searchInterventionsDto: SearchInterventionsDto = {
            ...searchInterventions,
        };

        serviceSearchInterventions(searchInterventionsDto)
            .then(result => {
                if (!isMounted) {
                    return;
                }

                if (result) {
                    manageIntervention(result);
                }
                setInterventions(result);
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }

                console.error('Erreur lors de la récupération des données');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                if (isMounted) {
                    setSearching(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [searchInterventions, props.confirmationProposalAssignment, showMessage]);

    //Méthode au changement de la page
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setSearchInterventions({
            ...searchInterventions,
            page: newPage,
        });
    };

    //Méthode au changement d'items par page
    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchInterventions({
            ...searchInterventions,
            page: 0,
            itemsPerPage: Number.parseInt(event.target.value, 10),
        });
    };

    //handler sort
    const handlerSortBy = (order: 'asc' | 'desc', sort: keyof InterventionDto) => {
        setSearchInterventions({
            ...searchInterventions,
            order: order,
            sort: sort,
        });
    };

    // Mise en place du formulaire à renvoyer côté back
    const handlerSearch = (paramForm: InterventionDto) => {
        if (searchInterventions) {
            const newSearchData: SearchInterventionsDto = {
                ...searchInterventions,
                paramReference: paramForm.reference,
                paramcodeTBB: paramForm.codeTBB,
                paramName: paramForm.name,
                paramPlace: paramForm.place,
                paramPlannedDate: paramForm.plannedDate,
                paramRealisationDate: paramForm.realisationDate,
                paramState: paramForm.state,
            };
            setSearchInterventions(newSearchData);
        }
    };

    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const headCells: HeadCellDto<InterventionDto, keyof InterventionDto>[] = [
        { id: 'reference', label: "Numéro d'intervention", sort: false, width: 15 },
        { id: 'codeTBB', label: 'Code TBB', sort: false, width: 10 },
        { id: 'name', label: 'Nom', sort: false, width: 20 },
        { id: 'place', label: 'Lieu', sort: true, width: 15 },
        { id: 'plannedDate', label: 'Date de planification', sort: false, width: 15 },
        { id: 'realisationDate', label: 'Date de réalisation', sort: true, width: 15 },
        { id: 'stateToShow', label: 'Etat', sort: false, width: 10 },
    ];

    //Premet de conditionner l'affichage du tableau des decisions
    const showTable = () => {
        // Données pas encore chargées
        if (interventions === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        // Données nulles
        if (interventions?.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucune donnée trouvée'} />;
        } else {
            return (
                <>
                    <StyledTable
                        data={interventions?.items}
                        searching={searching}
                        headCells={headCells}
                        totalItems={interventions?.totalItems}
                        page={searchInterventions?.page}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeItemsPerPage}
                        itemsPerPage={searchInterventions?.itemsPerPage}
                        handlerSortBy={handlerSortBy}
                        order={searchInterventions?.order}
                        sort={searchInterventions?.sort}
                    ></StyledTable>
                </>
            );
        }
    };

    //Retourne la vue
    return (
        <Grid container className={'tab-content intervention-ui'} rowSpacing={1}>
            <Grid item xs={12}>
                <Typography variant="h3" className={'title margin-top-50'}>
                    <BuildIcon /> Interventions
                </Typography>
            </Grid>
            <Grid item xs={12} md={1} />
            <Grid item xs={12} md={10}>
                <Stack>
                    {/*On affiche le formulaire*/}
                    <InterventionsForm handlerSearch={handlerSearch} searching={searching} />
                    {/* On affiche ensuite les réunions terminées */}
                    {showTable()}
                </Stack>
            </Grid>
        </Grid>
    );
}

// Prépare l'affichage de l'état dans le tableau.
function manageIntervention(result: ResultSearchDto<InterventionDto>) {
    for (const item of result.items) {
        switch (item.state) {
            case InterventionState.NO_PLANNED:
                item.stateToShow = (
                    <Chip label={`${item.state} `} size="small" style={{ backgroundColor: '#f65050', margin: '2px' }} />
                );
                break;
            case InterventionState.PLANNED:
                item.stateToShow = (
                    <Chip label={`${item.state} `} size="small" style={{ backgroundColor: '#FFAB00', margin: '2px' }} />
                );
                break;
            case InterventionState.FINISHED:
                item.stateToShow = (
                    <Chip label={`${item.state} `} size="small" style={{ backgroundColor: '#95be72', margin: '2px' }} />
                );
                break;
            default:
                break;
        }
    }
}
